import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useWedding } from '../../../context/WeddingContext'
import { ChevronLeft, ChevronRight, Mail, Users, BookOpen, Shirt } from 'lucide-react'
import InvitationDesigner from './InvitationDesigner'
import GuestsTab from '../guests/GuestsTab'
import DressCodeTab from './DressCodeTab'
import ProgrammeTab from './ProgrammeTab'
import PageTip from '../../../components/PageTip'
import { useSwipeTabs } from '../../../lib/swipe'

const TABS = [
  { id: 'invitations', label: 'Invitations', icon: Mail },
  { id: 'guests', label: 'Guests & RSVP', icon: Users },
  { id: 'dresscode', label: 'Dress Code', icon: Shirt },
  { id: 'programme', label: 'Programme', icon: BookOpen },
]

export default function EventDetail() {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const { events, wedding } = useWedding()
  const [activeTab, setActiveTab] = useState('invitations')
  const swipe = useSwipeTabs(TABS.map(t => t.id), activeTab, setActiveTab)

  // Show a hint chevron while the tab strip can still scroll right (Programme hides off-screen on small phones)
  const tabsRef = useRef(null)
  const [moreRight, setMoreRight] = useState(false)
  const checkScroll = () => { const el = tabsRef.current; if (el) setMoreRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4) }
  useEffect(() => { checkScroll(); window.addEventListener('resize', checkScroll); return () => window.removeEventListener('resize', checkScroll) }, [])

  const event = events.find(e => e.id === eventId)

  if (!event) return (
    <div style={{ textAlign: 'center', padding: '4rem 1rem' }}>
      <p style={{ fontFamily: 'Nunito Sans, sans-serif', color: '#6B6B6B' }}>Event not found.</p>
      <button className="btn-secondary" onClick={() => navigate('/dashboard/events')} style={{ marginTop: '1rem' }}>
        Back to Events
      </button>
    </div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* Back + Header */}
      <div>
        <button onClick={() => navigate('/dashboard/events')}
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.8rem', color: '#6B6B6B', marginBottom: '1rem', padding: 0 }}>
          <ChevronLeft size={16} /> Back to Events
        </button>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontFamily: 'Gibs, serif', fontWeight: 400, fontSize: 'clamp(1.4rem, 3vw, 1.8rem)', color: '#1A1A1A', margin: '0 0 0.25rem' }}>
              {event.type}
            </h1>
            <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.85rem', color: '#6B6B6B', margin: 0 }}>
              {event.date ? new Date(event.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : 'Date TBC'}
              {event.venue ? ` · ${event.venue}` : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ position: 'relative', borderBottom: '1px solid #E5E0D8' }}>
        <div ref={tabsRef} onScroll={checkScroll} style={{ display: 'flex', gap: '0', overflowX: 'auto', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setActiveTab(id)} style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.625rem 0.75rem', background: 'none', border: 'none', cursor: 'pointer', minHeight: 44, flexShrink: 0,
              fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.82rem', fontWeight: activeTab === id ? 700 : 500,
              color: activeTab === id ? '#C4826A' : '#6B6B6B', whiteSpace: 'nowrap',
              borderBottom: activeTab === id ? '2px solid #C4826A' : '2px solid transparent',
              marginBottom: '-1px', transition: 'all 0.15s'
            }}>
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>
        {/* "more tabs →" hint while the strip can still scroll right */}
        {moreRight && (
          <div onClick={() => { const el = tabsRef.current; if (el) el.scrollBy({ left: 140, behavior: 'smooth' }) }}
            style={{ position: 'absolute', right: 0, top: 0, bottom: 1, width: 44, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 2, background: 'linear-gradient(90deg, rgba(250,250,248,0), #FAFAF8 60%)', cursor: 'pointer', pointerEvents: 'auto' }}>
            <ChevronRight size={18} color="#C4826A" />
          </div>
        )}
      </div>

      <PageTip id="event-tabs" title="Your event, tab by tab"
        points={[
          'Invitations: design and send your invite card.',
          'Guests & RSVP: invite people to this event and track replies.',
          "Dress Code: set the attire and fabric — it's added to every guest message.",
          'Programme: build the order of service guests can follow.',
        ]}/>

      {/* Tab Content — swipe left/right on mobile to change tabs */}
      <div className="fade-in" {...swipe}>
        {activeTab === 'invitations' && <InvitationDesigner event={event} />}
        {activeTab === 'guests'      && <GuestsTab event={event} />}
        {activeTab === 'dresscode'   && <DressCodeTab event={event} />}
        {activeTab === 'programme'   && <ProgrammeTab event={event} weddingId={wedding?.id} />}
      </div>
    </div>
  )
}

